import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import { Autocomplete, Pagination } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSnackBar } from 'src/hooks';
import useResolution from 'src/hooks/useResolution';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { VoucherProduct } from 'src/redux/slices/voucher/types';
import { Category, Product } from 'src/types';
import { SelectProductsTable } from './SelectProductsTable';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';

interface Props {
  open: boolean;
  selectedProductIds: number[];
  discountAmount: number;
  discountCap: number;
  discountType: string;
  toggleOpen: () => void;
  saveSelectedProducts: (val: VoucherProduct[]) => void; // use later
}

const {
  actions: categoryActions,
  selectors: categorySelectors
} = slices.category;

const { actions: voucherActions, selectors: voucherSelectors } = slices.voucher;

export const SelectScopeProductsModal: FC<Props> = ({
  open = false,
  selectedProductIds = [],
  discountAmount = 0,
  discountCap = 0,
  discountType = 'amount',
  toggleOpen,
  saveSelectedProducts
}) => {
  const dispatch = useAppDispatch();
  const { isMobile, xs, sm } = useResolution();

  const categoryOptions = useAppSelector(categorySelectors.selectCategories);
  const productsMeta = useAppSelector(voucherSelectors.selectProductsMeta);

  const snackbar = useSnackBar();

  const [keyword, setKeyword] = useState<string>('');
  const [selectedProducts, setSelectedProducts] = useState<VoucherProduct[]>(
    []
  );
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState<boolean>(true);

  const isSmallScreen = useMemo(() => isMobile || xs || sm, [isMobile, sm, xs]);

  const onChangePage = async (_: any, page: number) => {
    const res = unwrapResult(
      await dispatch(
        voucherActions.getVoucherProductsThunk({
          limit: 10,
          page: page,
          discount_amount: discountAmount,
          discount_cap: discountCap,
          discount_type: discountType,

          ...(keyword && { keyword: keyword }),
          ...(categoryIds && { category_ids: categoryIds }),

          // filter out selected products by passing selected ids
          ...(selectedProductIds?.[0] && { product_ids: selectedProductIds })
        })
      )
    );

    if (!res.success) {
      console.error('Failed to fetch products', res.errors); // for devs
      snackbar.show({
        severity: 'error',
        message: 'Failed to load products. Please refresh page.'
      });
    }
  };

  const getCategoryOptions = async () => {
    setIsCategoriesLoading(true);
    const res = unwrapResult(
      await dispatch(categoryActions.getCategoriesThunk({}))
    );

    if (!res.success) {
      console.error('Failed to fetch categories', res.errors); // for devs
      snackbar.show({
        severity: 'error',
        message: 'Failed to load category options. Please refresh page.'
      });
    }

    setIsCategoriesLoading(false);
  };

  const searchProduct = async () => {
    const res = unwrapResult(
      await dispatch(
        voucherActions.getVoucherProductsThunk({
          limit: 10,
          page: 1,
          discount_amount: discountAmount,
          discount_cap: discountCap,
          discount_type: discountType,

          ...(keyword && { keyword: keyword }),
          ...(categoryIds?.[0] && { category_ids: categoryIds }),

          // filter out selected products by passing selected ids
          ...(selectedProductIds?.[0] && { product_ids: selectedProductIds })
        })
      )
    );

    if (!res.success) {
      console.error('Failed to fetch products', res.errors); // for devs
      snackbar.show({
        severity: 'error',
        message: 'Failed to load products. Please refresh page.'
      });
    }
  };

  const toggleSelectProductIds = (val: Product) => {
    const hasProduct = selectedProducts.find((p) => p.id === val.id);
    if (hasProduct) {
      const filteredProducts = selectedProducts.filter((p) => p.id !== val.id);
      setSelectedProducts(filteredProducts);
    } else {
      setSelectedProducts([...selectedProducts, val]);
    }
  };

  const onKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      searchProduct();
    }
  };

  useEffect(() => {
    if (open) {
      searchProduct();
    }

    if (!categoryOptions?.[0]) {
      getCategoryOptions();
    }

    setKeyword('');
    setCategoryIds([]);
    setSelectedProducts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      fullScreen={isSmallScreen}
      onClose={toggleOpen}
      PaperProps={{
        style: {
          ...(!isSmallScreen && { width: '60vw', maxWidth: 'none' })
        }
      }}
    >
      <DialogContent style={{ height: '80vh' }}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h5">Select Products</Typography>
          {isSmallScreen ? (
            <Button
              startIcon={<NavigateBeforeIcon />}
              onClick={toggleOpen}
              size="small"
              style={{ fontSize: '.8em' }}
            >
              Back
            </Button>
          ) : null}
        </Box>
        <Grid
          id="toolbar"
          container
          style={{
            padding: '1em 0',
            gap: '1em'
          }}
        >
          <Grid item xs="auto" style={{ minWidth: '20em' }}>
            <Autocomplete
              size={isSmallScreen ? 'small' : 'medium'}
              disabled={isCategoriesLoading && !categoryOptions?.[0]}
              options={categoryOptions || []}
              multiple
              fullWidth
              getOptionLabel={(category) => category.name}
              renderTags={(value: Category[], getTagProps) =>
                value.map((option: Category, index: number) => (
                  <Chip
                    key={`${option.name}${index}`}
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
              onChange={(_, newValue: Category[]) => {
                const ids = newValue?.[0] ? newValue?.map((c) => c.id) : [];
                setCategoryIds(ids);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size={isSmallScreen ? 'small' : 'medium'}
                  variant="outlined"
                  fullWidth
                  label={'Pick Categories'}
                />
              )}
            />
          </Grid>

          <Grid item xs={7} sm={6}>
            <TextField
              fullWidth
              size={isSmallScreen ? 'small' : 'medium'}
              variant="outlined"
              onChange={(e) => setKeyword(e.target.value)}
              label="Search Product"
              onKeyDown={onKeyDown}
            />
          </Grid>

          <Grid item xs>
            <Button
              onClick={searchProduct}
              variant="contained"
              fullWidth
              size={isSmallScreen ? 'small' : 'medium'}
              color="primary"
              style={{ height: '100%' }}
            >
              Apply Filter
            </Button>
          </Grid>
        </Grid>
        <SelectProductsTable
          selectedProducts={selectedProducts}
          toggleSelectProductIds={toggleSelectProductIds}
        />
      </DialogContent>
      <DialogActions style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'end',
            padding: '1em'
          }}
        >
          <Pagination
            siblingCount={isSmallScreen ? 0 : 1}
            count={productsMeta?.last_page || 0}
            page={productsMeta?.current_page || 1}
            onChange={onChangePage}
          />
        </div>

        <div
          style={{
            width: '100%',
            display: 'flex',
            padding: '1em',
            justifyContent: isSmallScreen ? 'end' : 'space-between'
          }}
        >
          {isSmallScreen ? null : (
            <Button
              variant="outlined"
              color="secondary"
              size={isSmallScreen ? 'small' : 'medium'}
              style={{ width: '15em' }}
              onClick={toggleOpen}
            >
              Cancel
            </Button>
          )}
          <Button
            disabled={!selectedProducts?.[0]}
            variant="contained"
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            onClick={() => saveSelectedProducts(selectedProducts)}
            style={{ width: '15em' }}
          >
            Save Selected
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
